/** Real client for /api/v1/health* (api/app/health/). Same origin + session cookie as the chat API. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import { HealthRateLimited } from './errors'
import type { HealthApi, HealthData, HealthSettings, HealthSummary } from './types'

export class HttpHealthApi implements HealthApi {
  constructor(
    private readonly base: string,
    // a manual run takes up to ~20 s on the server
    private readonly timeoutMs = 35000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const url = new URL(`${this.base.replace(/\/+$/, '')}/api/v1${path}`, window.location.origin)
      const res = await this.fetchImpl(url.toString(), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let detail = res.status === 403 ? 'เฉพาะผู้จัดการและเจ้าของเท่านั้น' : res.statusText || `HTTP ${res.status}`
        let retry: number | null = null
        try {
          const j = (await res.json()) as { detail?: unknown; retry_after_s?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
          if (typeof j.retry_after_s === 'number') retry = j.retry_after_s
        } catch {
          /* body not JSON */
        }
        if (res.status === 429) {
          const header = Number(res.headers.get('retry-after'))
          throw new HealthRateLimited(detail, Math.max(1, Math.ceil(retry ?? (Number.isFinite(header) && header > 0 ? header : 60))))
        }
        throw new ApiError(res.status, detail)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  get(): Promise<HealthData> {
    return this.req('GET', '/health')
  }

  summary(): Promise<HealthSummary> {
    return this.req('GET', '/health/summary')
  }

  run(): Promise<HealthData> {
    return this.req('POST', '/health/run')
  }

  saveSettings(body: { telegram_on: boolean }): Promise<HealthSettings> {
    return this.req('PUT', '/health/settings', body)
  }
}
